export interface BackendVersion {
  service: string;
  env: string;
  commit: string;
  builtAt: string | null;
}

export type BackendStatus =
  | { state: 'checking' }
  | { state: 'ok'; version: BackendVersion }
  | { state: 'unreachable'; reason: string };

export const API_BASE: string = import.meta.env.VITE_API_URL ?? '/api';

/** Validates the /version payload; throws with the field that was wrong. */
export function parseVersion(data: unknown): BackendVersion {
  if (typeof data !== 'object' || data === null) throw new Error('version: expected an object');
  const d = data as Record<string, unknown>;
  for (const key of ['service', 'env', 'commit'] as const) {
    if (typeof d[key] !== 'string') throw new Error(`version: "${key}" must be a string`);
  }
  if (d.builtAt !== null && typeof d.builtAt !== 'string') {
    throw new Error('version: "builtAt" must be a string or null');
  }
  return {
    service: d.service as string,
    env: d.env as string,
    commit: d.commit as string,
    builtAt: (d.builtAt as string | null) ?? null,
  };
}

export function shortCommit(commit: string): string {
  return /^[0-9a-f]{7,40}$/.test(commit) ? commit.slice(0, 7) : commit;
}

export async function fetchBackendStatus(
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<BackendStatus> {
  try {
    const res = await fetchImpl(`${API_BASE}/version`, { signal: signal ?? null, headers: { accept: 'application/json' } });
    if (!res.ok) return { state: 'unreachable', reason: `HTTP ${res.status}` };
    return { state: 'ok', version: parseVersion(await res.json()) };
  } catch (error) {
    if (signal?.aborted) throw error;
    return { state: 'unreachable', reason: error instanceof Error ? error.message : String(error) };
  }
}
